
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LucideIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { useAuth } from '@/contexts/auth';
import UserMenu from './UserMenu';

interface AuthDialogProps {
  userType: 'ahavexer' | 'customer';
  icon: LucideIcon;
  title: string;
  description: string;
  color?: string;
  className?: string;
  children?: React.ReactNode;
}

const AuthDialog: React.FC<AuthDialogProps> = ({
  userType,
  icon: Icon,
  title,
  description,
  color = 'currentColor',
  className,
  children,
}) => {
  const [open, setOpen] = React.useState(false);
  const { currentUser, userType: loggedInUserType } = useAuth();

  // If user is logged in as the specified userType or as admin (when userType is ahavexer), render UserMenu instead
  if (currentUser && (loggedInUserType === userType || (userType === 'ahavexer' && loggedInUserType === 'admin'))) {
    return (
      <UserMenu 
        userType={loggedInUserType as 'ahavexer' | 'customer' | 'admin'} 
        color={color} 
        className={className} 
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children ? (
          <div className={className}>{children}</div>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={className}
            aria-label={`${userType} account`}
          >
            <Icon color={color} size={20} strokeWidth={1.5} />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>
          
          <TabsContent value="login">
            <LoginForm userType={userType} onSuccess={() => setOpen(false)} />
          </TabsContent>
          
          <TabsContent value="register">
            <RegisterForm userType={userType} onSuccess={() => setOpen(false)} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
